import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Clock } from "lucide-react";
import { openCalendly } from "@/lib/calendly";

export const BOOKING_URL = "https://calendly.com/leprincemaxim/30min";

export const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";

export const MaskedLines = ({ lines, className = "", delay = 0 }) => (
  <span className={className}>
    {lines.map((line, i) => (
      <span className="mask-line" key={i}>
        <motion.span
          initial={{ y: "110%" }}
          animate={{ y: 0 }}
          transition={{ duration: 0.9, delay: delay + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </span>
);

export const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

export const BlogNav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#FAFAF7]/85 backdrop-blur-md border-b border-[#E7E5E4]" : "bg-transparent border-b border-transparent"
      }`}
      data-testid="blog-navigation"
    >
      <div className="container-main flex items-center justify-between py-4">
        <Link to="/" data-testid="blog-nav-logo" className="flex items-center" aria-label="Voveron — Home">
          <img src="/voveron-full-logo.png" alt="Voveron — US sweepstakes marketing agency" className="h-10 md:h-11 w-auto" width="220" height="60" />
        </Link>
        <div className="flex items-center gap-6 md:gap-8">
          <Link to="/" data-testid="blog-nav-home" className="hidden sm:block text-sm font-medium text-[#525252] hover:text-[#0A0A0A] transition-colors">
            Home
          </Link>
          <Link to="/blog" data-testid="blog-nav-blog" className="text-sm font-semibold text-[#7C3AED]">
            Blog
          </Link>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" onClick={openCalendly}>
            <button data-testid="blog-nav-cta" className="btn-primary px-5 py-2.5 text-sm font-semibold rounded-full">
              Book a Call
            </button>
          </a>
        </div>
      </div>
    </nav>
  );
};

export const BlogFooter = () => (
  <footer className="py-10 border-t border-[#E7E5E4] bg-white mt-24" data-testid="blog-footer">
    <div className="container-main flex flex-col md:flex-row items-center justify-between gap-4">
      <Link to="/" className="flex items-center" aria-label="Voveron — Home">
        <img src="/voveron-full-logo.png" alt="Voveron — US sweepstakes marketing agency" className="h-8 w-auto" width="180" height="48" />
      </Link>
      <p className="text-sm text-[#737373]">Performance marketing for US sweepstakes operators</p>
      <p className="text-sm text-[#737373]">© {new Date().getFullYear()} Voveron. All rights reserved.</p>
    </div>
  </footer>
);

export const Marquee = () => {
  const items = ["Paid Acquisition", "Creative Velocity", "Player LTV", "Compliance", "Scaling Systems", "Sweepstakes Only"];
  const track = [...items, ...items, ...items];
  return (
    <div className="blog-marquee" data-testid="blog-marquee">
      <div className="blog-marquee-track">
        {track.map((t, i) => (
          <span key={i}>
            {t}
            <em>✦</em>
          </span>
        ))}
      </div>
    </div>
  );
};

export const ArticleCard = ({ article, index = 0 }) => (
  <Reveal delay={(index % 3) * 0.08}>
    <Link
      to={`/blog/${article.slug}`}
      className="article-card block group"
      data-testid={`article-card-${article.slug}`}
    >
      <div className="card-img">
        {article.featured_image_url && (
          <img
            src={article.featured_image_url}
            alt={article.featured_image_alt || article.title}
            loading="lazy"
            decoding="async"
          />
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-[11px] mb-3 uppercase tracking-widest font-heading font-semibold">
          {article.category_name && <span className="text-[#7C3AED]">{article.category_name}</span>}
          <span className="w-1 h-1 bg-[#E7E5E4] rounded-full" />
          <span className="text-[#737373]">{formatDate(article.published_at)}</span>
        </div>
        <h3 className="font-heading text-xl font-bold text-[#0A0A0A] leading-snug mb-3 group-hover:text-[#7C3AED] transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-[#525252] leading-relaxed line-clamp-2 mb-5">{article.excerpt}</p>
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1.5 text-[#737373]">
            <Clock size={12} /> {article.reading_time} min read
          </span>
          <ArrowUpRight size={16} className="text-[#7C3AED] opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </Link>
  </Reveal>
);
